import * as React from 'react';
import { FC, PropsWithChildren, useCallback, useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';

import { Widget, WidgetItem } from '../Widget/Widget';
import { RGL_RESTORE_LAYOUT, RGL_WIDGET_RESIZE } from '../../AppConstants';
import { LocalStorageGet, LocalStorageSet } from '../../Services/Helpers';
import styles from './GridLayout.module.scss';
import { AppStoreContext } from '../../Stores/AppStore';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

type GridLayoutProps = {
    widgetsGroup?: string,
    widgets?: WidgetItem[]
    filters?: JSX.Element
    title?: string
    inBodyTitle?: boolean
    message?: string
    subFilters?:JSX.Element
}

const RGL_Prefix = `rgl_`;
const _getFromLS = (key: string): Layouts | null => {
    try {
        const val = LocalStorageGet<string>(`${RGL_Prefix}${key}`);
        return val ? JSON.parse(val)[key] : null;
    } catch (e) {
        return null;
    }
};

export const GridLayout: FC<PropsWithChildren<GridLayoutProps>> = observer(
    ({ widgetsGroup, inBodyTitle, widgets, filters, title, children, subFilters, message }) => {

        const { showGridReset, hideGridReset } = useContext(AppStoreContext);

        const _saveToLS = (key: string, value: Layouts) => {
            const keyStr = `${RGL_Prefix}${key}`;
            let valueStr = JSON.stringify({ [key]: value });
            LocalStorageSet(keyStr, valueStr);
        };

        const _updateContainerSizes = () => {
            document.dispatchEvent(new Event(RGL_WIDGET_RESIZE));
        };

        const layouts = React.useMemo(() => (widgetsGroup && _getFromLS(widgetsGroup)) || {}, [widgetsGroup])

        const _updateLayouts = useCallback((layouts: Layouts) => {
             // setLayouts(layouts);
             _updateContainerSizes();
        }, []);

        const _restoreLayout = useCallback(() => {
            const layouts = {};
            const keyStr = `${RGL_Prefix}${widgetsGroup}`;
            window.localStorage && delete window.localStorage[keyStr];
            widgetsGroup && _saveToLS(widgetsGroup, layouts);
            _updateLayouts(layouts);
        }, [widgetsGroup, _updateLayouts]);

        useEffect(() => {
            document.addEventListener(RGL_RESTORE_LAYOUT, _restoreLayout);
            return () => {
                document.removeEventListener(RGL_RESTORE_LAYOUT, _restoreLayout);
            };
        }, [_restoreLayout]);

        useEffect(() => {
            showGridReset();
            return () => {
                hideGridReset();
            };
        }, [showGridReset, hideGridReset]);

        const _onLayoutChange = (currentLayout: Layout[], allLayouts: Layouts) => {

            widgetsGroup && _saveToLS(widgetsGroup, allLayouts);
            _updateLayouts(allLayouts);
        };
        const hasWidgets = !!widgets && widgets.length > 0;

        return (
            <>
                        <div hidden={!filters} className={styles.filters}>
                            {!!filters && filters}
                        </div>
                        {!inBodyTitle &&
                            <div className={styles.headline}>
                                {title && <div className={styles.title}>{title}</div>}
                                {subFilters && <div className={styles.subFilter}>{subFilters}</div>}
                            </div>
                        }
                        {message ?
                            <div className={styles.message}>{message}</div> :
                            <>
                                {hasWidgets && (
                                    <div className={styles.grid}>
                                        <ResponsiveReactGridLayout
                                            layouts={layouts}
                                            isBounded={true}
                                            onLayoutChange={_onLayoutChange}
                                            onWidthChange={_updateContainerSizes}
                                            draggableCancel=".js-draggableCancel"
                                            draggableHandle=".js-draggableHandle"
                                            cols={{ lg: 6, md: 6, sm: 6, xs: 4, xxs: 2 }}

                                            rowHeight={15}
                                            containerPadding={[10, 10]}
                                            margin={[20, 20]}
                                        >
                                            {widgets?.map(el => {
                                                return (
                                                    <div key={el.gridItem.i} data-grid={{ ...el.gridItem, ...{ minH: 3 }, ...(layouts.lg ? layouts.lg?.find?.(it => it.i === el.gridItem.i) : {}) }}
                                                         style={el.customStyles}>
                                                        <Widget
                                                            showInfo={el.showInfo}
                                                            showStatus={el.showStatus}
                                                            label={el.label}
                                                            bodySpacing={el.bodySpacing}
                                                            isPrimaryHeaderStyle={el.isPrimaryHeaderStyle}
                                                            headerClassName={el.headerClassName}
                                                            bodyClassName={el.bodyClassName}
                                                        >
                                                            {(widgetSize, setStatus, showInfo) => el.node(widgetSize, setStatus, showInfo)}
                                                        </Widget>
                                                    </div>
                                                );
                                            })}
                                        </ResponsiveReactGridLayout>
                                    </div>
                                )}
                                {!hasWidgets && !inBodyTitle && children}
                                {!hasWidgets && inBodyTitle &&
                                    <div className={styles.body}>
                                        <div className={styles.headline}>
                                            {title && <div className={styles.title}>{title}</div>}
                                            {subFilters && <div className={styles.subFilter}>{subFilters}</div>}
                                        </div>
                                        {children}
                                    </div>
                                }
                            </>
                        }
                    </>
        );
    }
);
